import React, { useEffect, useState } from "react";
import { Sparkles, Loader2, Copy } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LangPicker } from "@/components/language/LangPicker";

const CONTENT_TYPES = [
  { id: "artist_bio", label: "Artist Bio" },
  { id: "news_story", label: "News Story" },
  { id: "blog_article", label: "Blog Article" },
  { id: "social_post", label: "Social Post" },
  { id: "podcast_notes", label: "Podcast Notes" },
  { id: "dj_break", label: "DJ Break" },
  { id: "radio_script", label: "Radio Script" },
];

export const LangContentTab = ({ overview, stationId }) => {
  const [ctype, setCtype] = useState("artist_bio");
  const [topic, setTopic] = useState("");
  const [langs, setLangs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);

  useEffect(() => { if (overview) setLangs(overview.active_languages || []); }, [overview]);

  const toggleLang = (c) => setLangs((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c]));

  const generate = async () => {
    if (!topic.trim()) return toast.error("Enter a topic first");
    if (!langs.length) return toast.error("Pick at least one language");
    setBusy(true); setResults([]);
    try {
      const r = await api.post("/language-center/generate", {
        station_id: stationId, content_type: ctype, topic, languages: langs });
      setResults(r.data.items || []);
      toast.success(`Generated in ${(r.data.items || []).filter((i) => !i.error).length} languages`);
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(det?.error ? "Not enough credits" : (typeof det === "string" ? det : "Generation failed"));
    } finally { setBusy(false); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  return (
    <div data-testid="lc-content-tab">
      <h2 className="font-display text-xl font-bold text-ink-primary">AI Content Generator</h2>
      <p className="text-sm text-ink-secondary mb-6">Create any content type in multiple languages simultaneously</p>
      <div className="grid lg:grid-cols-[260px_1fr] gap-6">
        <div className="space-y-4">
          <div className="rounded-xl border border-line bg-bg-surface p-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-3">Content Type</div>
            <div className="space-y-1">
              {CONTENT_TYPES.map((t) => (
                <button key={t.id} onClick={() => setCtype(t.id)} data-testid={`lc-ctype-${t.id}`}
                  className={`w-full rounded-md px-3 py-2 text-left text-sm transition ${
                    ctype === t.id ? "bg-brand-amber/10 border border-brand-amber/40 text-ink-primary font-semibold"
                      : "border border-transparent text-ink-secondary hover:bg-bg-elevated"}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <LangPicker all={overview?.languages?.all || []} selected={langs} onToggle={toggleLang} />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">Topic / Subject</div>
          <textarea
            value={topic} onChange={(e) => setTopic(e.target.value)} rows={4} data-testid="lc-content-topic"
            placeholder="Enter your topic, artist name, story, or any content brief…"
            className="w-full rounded-xl border border-line bg-bg-surface p-4 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-brand-amber/50"
          />
          <button
            onClick={generate} disabled={busy} data-testid="lc-content-generate-btn"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 px-4 py-3 text-sm font-bold text-white hover:brightness-110 transition disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {busy ? "Generating…" : `Generate in ${langs.length} Language${langs.length === 1 ? "" : "s"}`}
          </button>

          <div className="mt-6 space-y-4">
            {results.map((r) => (
              <div key={r.language} className="rounded-xl border border-line bg-bg-surface p-4" data-testid={`lc-content-result-${r.language}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-mono text-[9px] uppercase rounded bg-bg-elevated px-1.5 py-0.5 text-ink-secondary">{r.language}</span>
                  <span className="text-sm font-semibold text-ink-primary">{r.name}</span>
                  <span className="text-xs text-ink-muted">{r.native}</span>
                  <span className="flex-1" />
                  {!r.error && (
                    <button onClick={() => copy(r.output)} data-testid={`lc-content-copy-${r.language}`}
                      className="inline-flex items-center gap-1 rounded-md border border-line px-2 py-1 text-[11px] text-ink-secondary hover:text-ink-primary transition">
                      <Copy className="h-3 w-3" /> Copy
                    </button>
                  )}
                </div>
                {r.error
                  ? <div className="text-xs text-rose-400">Failed: {r.error}</div>
                  : <div className="whitespace-pre-wrap text-sm text-ink-secondary leading-relaxed">{r.output}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
